import { AlertTriangle } from 'react-feather';
import { useTranslation } from 'react-i18next';

import { ParticipantAttribute, RegistrationDetailsInput } from '__generated__/graphql';
import Address, { containsAddress } from '../Common/Address';
import PhoneNumber from '../PhoneNumber/PhoneNumber';
import UI from '../UI';
import useLocale from '../useLocale';

interface RegistrationAttributeProps {
  registration: RegistrationDetailsInput;
  attribute: ParticipantAttribute;
  showColumns?: boolean;
  showEmptyWarning?: boolean;
}

const RegistrationAttribute = ({
  registration, attribute, showColumns, showEmptyWarning = false,
}: RegistrationAttributeProps) => {
  const { t } = useTranslation('common');
  const { formatDate, parseDate } = useLocale();

  const columns = showColumns ? 'minmax(140px, 1fr) 2fr' : '1fr';
  const gutter = showColumns ? 0.5 : 0;

  const emptyValue = showEmptyWarning ? (
    <UI.Span sc={{ color: 'error' }}>
      <UI.Icon>
        <AlertTriangle />
      </UI.Icon>
      {' '}
      {t('participant_attributes.not_entered')}
    </UI.Span>
  ) : (
    <UI.Span sc={{ muted: true }}> &ndash; </UI.Span>
  );

  const hasAddress = containsAddress(registration);

  if (attribute === ParticipantAttribute.date_of_birth) {
    return (
      <UI.GridContainer sc={{ columns, gutter }}>
        <UI.Div>
          <UI.Legend sc={{ fontWeight: 500 }}>
            {t('participant_attributes.date_of_birth')}
          </UI.Legend>
        </UI.Div>
        <UI.Div>
          {registration.date_of_birth && formatDate(
            parseDate(registration.date_of_birth, { format: 'internal_date' }),
            { format: 'date_of_birth' },
          )}
          {!registration.date_of_birth && emptyValue}
        </UI.Div>
      </UI.GridContainer>
    );
  }

  if (attribute === ParticipantAttribute.gender) {
    return (
      <UI.GridContainer sc={{ columns, gutter }}>
        <UI.Div>
          <UI.Legend sc={{ fontWeight: 500 }}>
            {t('participant_attributes.gender')}
          </UI.Legend>
        </UI.Div>
        <UI.Div>
          {registration.gender && t(`genders.${registration.gender}`)}
          {!registration.gender && emptyValue}
        </UI.Div>
      </UI.GridContainer>
    );
  }

  if (attribute === ParticipantAttribute.nationality) {
    return (
      <UI.GridContainer sc={{ columns, gutter }}>
        <UI.Div>
          <UI.Legend sc={{ fontWeight: 500 }}>
            {t('participant_attributes.nationality')}
          </UI.Legend>
        </UI.Div>
        <UI.Div>
          {registration.nationality && t(`countries.${registration.nationality}`)}
          {!registration.nationality && emptyValue}
        </UI.Div>
      </UI.GridContainer>
    );
  }

  if (attribute === ParticipantAttribute.address) {
    return (
      <UI.GridContainer sc={{ columns, gutter }}>
        <UI.Div>
          <UI.Legend sc={{ fontWeight: 500 }}>
            {t('participant_attributes.address')}
          </UI.Legend>
        </UI.Div>
        <UI.Div>
          {hasAddress && <Address {...registration} />}
          {!hasAddress && emptyValue}
        </UI.Div>
      </UI.GridContainer>
    );
  }

  if (attribute === ParticipantAttribute.phone) {
    return (
      <UI.GridContainer sc={{ columns, gutter }}>
        <UI.Div>
          <UI.Legend sc={{ fontWeight: 500 }}>
            {t('participant_attributes.phone')}
          </UI.Legend>
        </UI.Div>
        <UI.Div>
          {registration.phone && <PhoneNumber phoneNumber={registration.phone} />}
          {!registration.phone && emptyValue}
        </UI.Div>
      </UI.GridContainer>
    );
  }

  if (attribute === ParticipantAttribute.emergency_phone) {
    return (
      <UI.GridContainer sc={{ columns, gutter }}>
        <UI.Div>
          <UI.Legend sc={{ fontWeight: 500 }}>
            {t('participant_attributes.emergency_phone')}
          </UI.Legend>
        </UI.Div>
        <UI.Div>
          {registration.emergency_phone && <PhoneNumber phoneNumber={registration.emergency_phone} />}
          {!registration.emergency_phone && emptyValue}
        </UI.Div>
      </UI.GridContainer>
    );
  }

  return <></>;
};

export default RegistrationAttribute;
